import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Button,
  Card,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Fade,
  IconButton,
  InputLabel,
  List,
  ListItem,
  makeStyles,
  Popover,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Paper,
  withStyles
} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import CloseIcon from '@material-ui/icons/Close';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import { useDispatch, useSelector } from 'react-redux';
import {
  getServicePackagePrices,
  createServicePackagePrice,
  updateServicePackagePrice,
  deleteServicePackagePrices
} from 'src/features/servicePackagePriceSlice';
import { getListServicePackages } from 'src/features/servicePackageSlice';
import SearchInput from 'src/app/components/input/SearchInput';
import PerfectScrollbar from 'react-perfect-scrollbar';
import ModalConfirm from 'src/app/components/ModalConfirm';

const formatCurrency = amount => {
  if (amount === null || amount === undefined || amount === '') return '-';
  return Number(amount).toLocaleString('vi-VN') + ' ₫';
};

const StyledTableCell = withStyles(theme => ({
  head: {
    backgroundColor: theme.palette.common.white,
    color: theme.palette.common.black,
    whiteSpace: 'nowrap'
  },
  body: {
    fontSize: 14,
    whiteSpace: 'nowrap'
  },
  root: {
    padding: '8px'
  }
}))(TableCell);

const renderPackageTypeBadge = (name, type) => {
  const lowercaseName = (name || '').toLowerCase();
  const isActivation = type === 'KICH_HOAT' || lowercaseName.includes('kích hoạt') || lowercaseName.includes('active') || lowercaseName.includes('kh');
  const isExtension = type === 'GIA_HAN' || lowercaseName.includes('gia hạn') || lowercaseName.includes('extend') || lowercaseName.includes('gh');

  let label = 'Cả hai';
  let color = '#757575';
  let backgroundColor = '#f5f5f5';

  if (isActivation && !isExtension) {
    label = 'Kích hoạt';
    color = '#2e7d32';
    backgroundColor = '#e8f5e9';
  } else if (isExtension && !isActivation) {
    label = 'Gia hạn';
    color = '#1565c0';
    backgroundColor = '#e3f2fd';
  }

  return (
    <span
      style={{
        padding: '3px 8px',
        borderRadius: '12px',
        fontSize: '12px',
        fontWeight: 600,
        color: color,
        backgroundColor: backgroundColor,
        display: 'inline-block',
        whiteSpace: 'nowrap'
      }}
    >
      {label}
    </span>
  );
};

export default function ConfigServicePackagePanel({ agencySelected, hideTitle, isRetail }) {
  const classes = useStyles();
  const dispatch = useDispatch();

  const labelPrice = 'Giá gói dịch vụ';
  const labelPriceVnd = isRetail ? 'Giá cước bán lẻ (VNĐ)' : 'Giá cước đại lý (VNĐ)';
  const labelPricePlaceholder = isRetail ? 'Nhập giá bán lẻ cho khách...' : 'Nhập giá cước cho đại lý...';
  const labelPriceEditPlaceholder = isRetail ? 'Nhập giá bán lẻ mới...' : 'Nhập giá cước đại lý mới...';
  const labelTitleManage = isRetail 
    ? `Quản trị gói dịch vụ khách lẻ (Đại lý ${agencySelected?.name || ''})` 
    : `Cấu hình gói dịch vụ đại lý (Đại lý ${agencySelected?.name || ''})`;
  const labelAddTitle = isRetail ? 'Thêm gói dịch vụ khách lẻ' : 'Thêm gói dịch vụ đại lý';
  const labelEditTitle = isRetail ? 'Chỉnh sửa gói dịch vụ khách lẻ' : 'Chỉnh sửa gói dịch vụ đại lý';

  const listServicePackagePrices = useSelector(
    state => state.servicePackagePriceSlice.listServicePackagePrices
  );
  const loadingPrices = useSelector(
    state => state.servicePackagePriceSlice.loading
  );
  const listAllPackages = useSelector(
    state => state.servicePackageSlice.listServicePackages
  );

  // States
  const [searchKeywords, setSearchKeywords] = useState('');
  const [openAddModal, setOpenAddModal] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [itemCurrent, setItemCurrent] = useState(null);

  // Modals state
  const [selectedPkg, setSelectedPkg] = useState(null);
  const [customPrice, setCustomPrice] = useState('');
  const [editingPrice, setEditingPrice] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const [openDeleteConfirm, setOpenDeleteConfirm] = useState(false);
  const [deleteConfirmContent, setDeleteConfirmContent] = useState('');
  const [deleteTargetIds, setDeleteTargetIds] = useState([]);

  const [anchorEl, setAnchorEl] = useState(null);

  const filteredPricesList = (listServicePackagePrices || []).filter(item => {
    if (!searchKeywords) return true;
    const pkg = item.service_package || item.servicePackage || item;
    const pkgName = (pkg?.name || '').toLowerCase();
    const devType = (pkg?.deviceType?.name || '').toLowerCase();
    const searchVal = searchKeywords.toLowerCase();
    return pkgName.includes(searchVal) || devType.includes(searchVal);
  });

  const [rowsSelected, setRowsSelected] = useState([]);

  const isChecked = id => {
    return rowsSelected.indexOf(id) !== -1;
  };

  const select = id => {
    if (rowsSelected.indexOf(id) === -1) {
      setRowsSelected([id, ...rowsSelected]);
    } else {
      setRowsSelected(rowsSelected.filter(i => i !== id));
    }
  };

  const checkAll = () => {
    if (rowsSelected.length > 0) {
      setRowsSelected([]);
    } else if (filteredPricesList.length > 0) {
      const all = filteredPricesList.map(item => item.id);
      setRowsSelected(all);
    }
  };

  const fetchPrices = () => {
    setRowsSelected([]);
    if (agencySelected?.id) {
      dispatch(
        getServicePackagePrices({
          owner_type: 'agency',
          owner_id: agencySelected.id,
          page: 1,
          page_size: 100
        })
      );
    }
  };

  const handleDeleteMulti = () => {
    if (rowsSelected.length > 0) {
      setDeleteTargetIds(rowsSelected);
      setDeleteConfirmContent(`Bạn có chắc chắn muốn xóa ${rowsSelected.length} cấu hình gói cước đã chọn?`);
      setOpenDeleteConfirm(true);
    }
  };

  useEffect(() => {
    if (agencySelected?.id) {
      fetchPrices();
      dispatch(getListServicePackages({ page: 1, page_size: 1000 }));
    }
  }, [agencySelected]);

  // Filtering out packages that are already configured for this agency and excluding activation packages
  const availablePackages = (listAllPackages || []).filter(
    pkg => {
      const pkgName = pkg?.name || '';
      const isActivation = pkg?.type ? pkg.type === 'KICH_HOAT' : /kích hoạt|active|kh/i.test(pkgName);
      if (isActivation) return false;

      return !(listServicePackagePrices || []).some(
        priceRec => {
          const innerPkg = priceRec.service_package || priceRec.servicePackage || priceRec;
          return Number(innerPkg?.id || priceRec.service_package_id) === Number(pkg.id);
        }
      );
    }
  );

  const handleAddConfig = async () => {
    if (!selectedPkg) {
      setErrorMsg('Vui lòng chọn gói cước');
      return;
    }
    const priceNum = Number(customPrice.replace(/\D/g, ''));
    if (!customPrice || priceNum < 0) {
      setErrorMsg('Vui lòng nhập giá cước hợp lệ');
      return;
    }

    setSubmitting(true);
    setErrorMsg('');
    try {
      const resultAction = await dispatch(
        createServicePackagePrice({
          owner_type: 'agency',
          owner_id: agencySelected.id,
          service_package_id: selectedPkg.id,
          price: priceNum
        })
      );
      if (createServicePackagePrice.fulfilled.match(resultAction)) {
        fetchPrices();
        setOpenAddModal(false);
        setSelectedPkg(null);
        setCustomPrice('');
      } else {
        setErrorMsg(resultAction.payload?.message || 'Có lỗi xảy ra');
      }
    } catch (e) {
      setErrorMsg('Có lỗi xảy ra khi tạo cấu hình');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditClick = () => {
    if (itemCurrent) {
      const pkg = itemCurrent.service_package || itemCurrent.servicePackage || itemCurrent;
      const price = itemCurrent.price ?? pkg.cost;
      setEditingPrice(String(price));
      setOpenEditModal(true);
    }
    handleCloseMenu();
  };

  const handleSaveEdit = async () => {
    if (!itemCurrent) return;
    const priceNum = Number(editingPrice.replace(/\D/g, ''));
    if (!editingPrice || priceNum < 0) {
      return;
    }
    setSubmitting(true);
    setErrorMsg('');
    try {
      const resultAction = await dispatch(
        updateServicePackagePrice({
          id: itemCurrent.id,
          data: { price: priceNum }
        })
      );
      if (updateServicePackagePrice.fulfilled.match(resultAction)) {
        setOpenEditModal(false);
        setItemCurrent(null);
        fetchPrices();
      } else {
        setErrorMsg(resultAction.payload?.message || 'Có lỗi xảy ra');
      }
    } catch (e) {
      console.error(e);
      setErrorMsg('Có lỗi xảy ra khi lưu cấu hình');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = () => {
    if (itemCurrent) {
      setDeleteTargetIds([itemCurrent.id]);
      setDeleteConfirmContent('Bạn có chắc chắn muốn xóa cấu hình gói cước này?');
      setOpenDeleteConfirm(true);
    }
    handleCloseMenu();
  };

  const handleConfirmDelete = async () => {
    if (!deleteTargetIds || deleteTargetIds.length === 0) return;
    setSubmitting(true);
    try {
      const resultAction = await dispatch(deleteServicePackagePrices(deleteTargetIds));
      if (deleteServicePackagePrices.fulfilled.match(resultAction)) {
        setOpenDeleteConfirm(false);
        setRowsSelected([]);
        setDeleteTargetIds([]);
        fetchPrices();
      }
    } catch (e) {
      console.error(e);
    } finally {
      setSubmitting(false);
    }
  };

  const handleClickMenu = (event, item) => {
    setAnchorEl(event.currentTarget);
    setItemCurrent(item);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  return (
    <Box className={classes.root}>
      {/* TOOLBAR */}
      <Box className={classes.groupSearch}>
        {!hideTitle ? (
          <>
            <div className={classes.groupSearchLeft}>
              <Typography className={classes.title}>
                {labelTitleManage}
              </Typography>
            </div>
            <Box style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
              <SearchInput
                value={searchKeywords}
                onChange={val => setSearchKeywords(val)}
                onSearch={val => setSearchKeywords(val)}
                className={'searchInputDevice'}
                placeholder="Tên gói hoặc loại thiết bị..."
              />
              <Button
                style={{ color: '#ffff', height: '40px', textTransform: 'none', padding: '0px 1em' }}
                color="secondary"
                variant="contained"
                onClick={() => {
                  setErrorMsg('');
                  setSelectedPkg(null);
                  setCustomPrice('');
                  setOpenAddModal(true);
                }}
              >
                Thêm gói dịch vụ
              </Button>
            </Box>
          </>
        ) : (
          <>
            <SearchInput
              value={searchKeywords}
              onChange={val => setSearchKeywords(val)}
              onSearch={val => setSearchKeywords(val)}
              className={'searchInputDevice'}
              placeholder="Tên gói hoặc loại thiết bị..."
            />
            <Button
              style={{ color: '#ffff', height: '40px', textTransform: 'none', padding: '0px 1em' }}
              color="secondary"
              variant="contained"
              onClick={() => {
                setErrorMsg('');
                setSelectedPkg(null);
                setCustomPrice('');
                setOpenAddModal(true);
              }}
            >
              Thêm gói dịch vụ
            </Button>
          </>
        )}
      </Box>

      {/* MULTI DELETE TOOLBAR */}
      {rowsSelected.length > 0 && (
        <Fade in={rowsSelected.length > 0}>
          <Box
            style={{ fontSize: '14px', padding: '6px 4px', display: 'flex', alignItems: 'center', marginTop: '16px' }}
          >
            <span>
              <Checkbox
                checked={rowsSelected.length > 0}
                onChange={() => checkAll()}
                color="primary"
                inputProps={{ 'aria-label': 'secondary checkbox' }}
              />
            </span>
            <span
              onClick={handleDeleteMulti}
              className="mx-2 btnDevice"
              style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', color: '#D9042F' }}
            >
              <img
                src={`/static/iconSvg/delete-icon.svg`}
                alt="delete"
                style={{ paddingRight: '5px', paddingBottom: '4px' }}
              />
              <span className="pt-1"> Xóa </span>
            </span>
          </Box>
        </Fade>
      )}

      {/* RESULTS TABLE */}
      <Card className={`${classes.card} mt-3`}>
        <PerfectScrollbar>
          {loadingPrices ? (
            <Box display="flex" justifyContent="center" py={5}>
              <CircularProgress size={36} style={{ color: '#D9042F' }} />
            </Box>
          ) : filteredPricesList.length === 0 ? (
            <div className="text-center pb-5 pt-5">
              <img src="/static/empty.png" className="justify-content-center" alt="empty" />
              <h4>Danh sách trống</h4>
            </div>
          ) : (
            <Box style={{ overflowX: 'auto' }}>
              <Table size="medium">
                {rowsSelected.length === 0 && filteredPricesList.length > 0 && (
                  <TableHead>
                    <TableRow>
                      <StyledTableCell width={60}>
                        <Checkbox
                          checked={rowsSelected.length > 0}
                          onChange={() => checkAll()}
                          color="primary"
                          inputProps={{
                            'aria-label': 'secondary checkbox'
                          }}
                        />
                      </StyledTableCell>
                      <StyledTableCell>Tên gói dịch vụ</StyledTableCell>
                      <StyledTableCell>Loại thiết bị</StyledTableCell>
                      <StyledTableCell>{labelPrice}</StyledTableCell>
                      <StyledTableCell>Phiên bản</StyledTableCell>
                      <StyledTableCell>Hạn gói dịch vụ</StyledTableCell>
                      <StyledTableCell>Mô tả</StyledTableCell>
                      <StyledTableCell width={60}></StyledTableCell>
                    </TableRow>
                  </TableHead>
                )}
                <TableBody>
                  {filteredPricesList.map(item => {
                    const pkg = item.service_package || item.servicePackage || item;
                    const price = item.price ?? pkg.cost;

                    return (
                      <TableRow key={item.id} hover>
                        <StyledTableCell
                          style={{ maxWidth: '50px' }}
                          onClick={() => select(item.id)}
                        >
                          <Checkbox
                            checked={isChecked(item.id)}
                            onChange={() => select(item.id)}
                            color="primary"
                            inputProps={{
                              'aria-label': 'secondary checkbox'
                            }}
                          />
                        </StyledTableCell>
                        <StyledTableCell style={{ fontWeight: 500 }}>
                          {pkg.name}
                        </StyledTableCell>
                        <StyledTableCell>
                          {pkg.deviceType?.name || pkg.device_type_id || '--'}
                        </StyledTableCell>
                        <StyledTableCell>
                          {formatCurrency(price)}
                        </StyledTableCell>
                        <StyledTableCell>
                          {pkg.version || '--'}
                        </StyledTableCell>
                        <StyledTableCell>
                          {pkg.number_of_day ? `${pkg.number_of_day} ngày` : (pkg.day_limit ? `${pkg.day_limit} ngày` : '--')}
                        </StyledTableCell>
                        <StyledTableCell>
                          {pkg.description || '--'}
                        </StyledTableCell>
                        <StyledTableCell>
                          <span style={{ color: '#AEB1C5' }}>
                            <MoreVertIcon
                              aria-describedby={`menu-retail-${item.id}`}
                              className={`cursor-pointer hover-red`}
                              onClick={e => handleClickMenu(e, item)}
                            />
                          </span>
                        </StyledTableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
          )}
        </PerfectScrollbar>
      </Card>

      {/* MORE ACTIONS POPOVER */}
      <Popover
        id="menu-retail-action"
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleCloseMenu}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
      >
        <Typography>
          <List>
            <ListItem
              onClick={handleEditClick}
              className="border-bottom list-menu-device cursor-pointer"
            >
              Chỉnh sửa
            </ListItem>
            <ListItem
              onClick={handleDelete}
              className="cursor-pointer list-menu-device"
            >
              <span className="text-danger">Xóa</span>
            </ListItem>
          </List>
        </Typography>
      </Popover>

      {/* DIALOG ADD PACKAGE CONFIG */}
      <Dialog open={openAddModal} onClose={() => setOpenAddModal(false)} maxWidth="sm" fullWidth>
        <DialogTitle disableTypography className={classes.dialogTitle}>
          <Typography variant="h6" style={{ fontWeight: 700, color: '#fff' }}>
            {labelAddTitle}
          </Typography>
          <IconButton className={classes.closeBtn} onClick={() => setOpenAddModal(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box mb={2}>
            <InputLabel style={{ marginBottom: 8, fontWeight: 500 }}>Chọn gói cước <span className="text-danger">*</span></InputLabel>
            <Autocomplete
              options={availablePackages}
              getOptionLabel={opt => `${opt.name} (${opt.deviceType?.name || 'Mặc định'})`}
              value={selectedPkg}
              onChange={(_, val) => setSelectedPkg(val)}
              renderInput={params => (
                <TextField
                  {...params}
                  variant="outlined"
                  size="small"
                  placeholder="Chọn gói cước..."
                />
              )}
            />
          </Box>
          <Box mb={2}>
            <InputLabel style={{ marginBottom: 8, fontWeight: 500 }}>{labelPriceVnd} <span className="text-danger">*</span></InputLabel>
            <TextField
              variant="outlined"
              size="small"
              placeholder={labelPricePlaceholder}
              value={customPrice ? Number(customPrice.replace(/\D/g, '')).toLocaleString('vi-VN') : ''}
              onChange={e => setCustomPrice(e.target.value.replace(/\D/g, ''))}
              fullWidth
            />
          </Box>
          {errorMsg && (
            <Typography color="error" variant="body2" style={{ marginBottom: 8 }}>
              {errorMsg}
            </Typography>
          )}
        </DialogContent>
        <DialogActions style={{ padding: '12px 24px' }}>
          <Button onClick={() => setOpenAddModal(false)} style={{ textTransform: 'none', color: '#666' }}>
            Thoát
          </Button>
          <Button
            variant="contained"
            onClick={handleAddConfig}
            disabled={submitting || !selectedPkg || !customPrice}
            style={{
              textTransform: 'none',
              backgroundColor: (submitting || !selectedPkg || !customPrice) ? undefined : '#D9042F',
              color: (submitting || !selectedPkg || !customPrice) ? undefined : '#fff'
            }}
          >
            {submitting ? 'Đang thêm...' : 'Thêm mới'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* DIALOG EDIT PACKAGE CONFIG */}
      <Dialog open={openEditModal} onClose={() => setOpenEditModal(false)} maxWidth="sm" fullWidth>
        <DialogTitle disableTypography className={classes.dialogTitle}>
          <Typography variant="h6" style={{ fontWeight: 700, color: '#fff' }}>
            {labelEditTitle}
          </Typography>
          <IconButton className={classes.closeBtn} onClick={() => setOpenEditModal(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box mb={2}>
            <InputLabel style={{ marginBottom: 8, fontWeight: 500 }}>Tên gói dịch vụ</InputLabel>
            <TextField
              variant="outlined"
              size="small"
              value={itemCurrent?.service_package?.name || itemCurrent?.servicePackage?.name || ''}
              disabled
              fullWidth
            />
          </Box>
          <Box mb={2}>
            <InputLabel style={{ marginBottom: 8, fontWeight: 500 }}>{labelPriceVnd} <span className="text-danger">*</span></InputLabel>
            <TextField
              variant="outlined"
              size="small"
              placeholder={labelPriceEditPlaceholder}
              value={editingPrice ? Number(editingPrice.replace(/\D/g, '')).toLocaleString('vi-VN') : ''}
              onChange={e => setEditingPrice(e.target.value.replace(/\D/g, ''))}
              fullWidth
            />
          </Box>
          {errorMsg && (
            <Typography color="error" variant="body2" style={{ marginBottom: 8 }}>
              {errorMsg}
            </Typography>
          )}
        </DialogContent>
        <DialogActions style={{ padding: '12px 24px' }}>
          <Button onClick={() => setOpenEditModal(false)} style={{ textTransform: 'none', color: '#666' }}>
            Thoát
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveEdit}
            disabled={submitting || !editingPrice}
            style={{
              textTransform: 'none',
              backgroundColor: (submitting || !editingPrice) ? undefined : '#D9042F',
              color: (submitting || !editingPrice) ? undefined : '#fff'
            }}
          >
            {submitting ? 'Đang lưu...' : 'Lưu lại'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* DIALOG CONFIRM DELETE */}
      <ModalConfirm
        title="Xác nhận xóa"
        open={openDeleteConfirm}
        setOpen={setOpenDeleteConfirm}
        textOk="Xóa"
        textCancel="Hủy"
        content={deleteConfirmContent}
        callbackOk={handleConfirmDelete}
        loading={submitting}
      />
    </Box>
  );
}

const useStyles = makeStyles(theme => ({
  root: {
    width: '100%'
  },
  card: {
    position: 'relative',
    '&.MuiPaper-elevation1': {
      boxShadow: 'none'
    }
  },
  title: {
    fontSize: '24px',
    fontWeight: '600',
    color: '#0C1132',
    [theme.breakpoints.down('sm')]: {
      fontSize: '18px',
      fontWeight: '500'
    }
  },
  groupSearch: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    minWidth: '50%',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      alignItems: 'flex-start',
      width: '100%',
      gap: '8px'
    }
  },
  groupSearchLeft: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start'
  },
  dialogTitle: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 24px',
    backgroundColor: '#D9042F',
    color: '#fff'
  },
  closeBtn: { color: '#fff' }
}));
